import { Dispatch, SetStateAction, useMemo, useState } from "react";

import usePreviousJobPartList from "../../data/usePreviousJobPartList";
import { ILead } from "../../models/Lead";
import { IPreviousJobPartList } from "../../models/Job";
import { CategoryWithParts, IPartAssembly, IPartCategory } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import useNotification from "../notifications/useNotifications";
import { getPreviousPartListMatchLabel } from "./PreviousPartListModal";

interface UsePreviousPartListCategoriesArgs {
  lead: ILead | null;
  vehicle: IVehicle | null;
  categories: CategoryWithParts[];
  allCategories: IPartCategory[] | undefined;
  assemblies: IPartAssembly[] | undefined;
  selectedByTableId: Record<string, CategoryWithParts[]>;
  setCategories: Dispatch<SetStateAction<CategoryWithParts[]>>;
  onTableSelectionChange: (tableId: string, categories: CategoryWithParts[]) => void;
  getCategoryWithMasterNames: (category: IPartCategory) => IPartCategory;
}

export default function usePreviousPartListCategories({
  lead,
  vehicle,
  categories,
  allCategories,
  assemblies,
  selectedByTableId,
  setCategories,
  onTableSelectionChange,
  getCategoryWithMasterNames,
}: UsePreviousPartListCategoriesArgs) {
  const { previousPartLists } = usePreviousJobPartList();
  const { addNotification } = useNotification();
  const [previousPartListOpen, setPreviousPartListOpen] = useState(false);
  const [previousPartListsData, setPreviousPartListsData] = useState<IPreviousJobPartList[]>([]);
  const [selectedPreviousJobId, setSelectedPreviousJobId] = useState<string | null>(null);

  const previousPartListData = useMemo<IPreviousJobPartList | null>(() => {
    if (previousPartListsData.length === 0) return null;
    return previousPartListsData.find(p => p.jobId === selectedPreviousJobId) ?? previousPartListsData[0];
  }, [previousPartListsData, selectedPreviousJobId]);

  const getPreviousPartListCategory = (partCategoryId: string) => {
    const existingCategory = categories?.find(c => c.category.id === partCategoryId);
    const masterCategory = allCategories?.find(c => c.id === partCategoryId);

    return {
      existingCategory,
      category: existingCategory?.category ?? masterCategory,
    };
  };

  const fetchPreviousPartListCategories = async () => {
    const sourceVehicle = lead?.vehicle ?? vehicle;
    if (!sourceVehicle?.id) return;

    try {
      const response = await previousPartLists.mutateAsync(sourceVehicle.id);
      const lists = (response.data ?? []).filter(p => p.categories.length > 0);

      if (!response.success || lists.length === 0) {
        addNotification({ primaryText: "No Previous Part List Found", variant: "info" });
        return;
      }

      setPreviousPartListsData(lists);
      setSelectedPreviousJobId(lists[0].jobId);
      setPreviousPartListOpen(true);
    } catch (error) {
      console.error("Previous part list lookup failed:", error);
      addNotification({ primaryText: "Previous Part List Lookup Failed", variant: "error" });
    }
  };

  const applyPreviousPartListCategories = () => {
    if (!previousPartListData) return;

    try {
      const currentSelections = { ...selectedByTableId };
      const previousPricesByCategoryId = new Map<string, { price?: number, lastSoldPrice?: number | null }>();

      previousPartListData.categories.forEach(previousCategory => {
        const { existingCategory, category } = getPreviousPartListCategory(previousCategory.partCategoryId);
        if (!category) return;

        const mappedPrice = previousCategory.price ?? existingCategory?.price ?? undefined;
        previousPricesByCategoryId.set(previousCategory.partCategoryId, {
          price: mappedPrice,
          lastSoldPrice: previousCategory.lastSoldPrice ?? existingCategory?.lastSoldPrice,
        });

        const enrichedCategory = getCategoryWithMasterNames(category);
        const isMech =
          enrichedCategory.mechOnly === false && enrichedCategory.partOnly === true ? false : true;

        const targetCategory: CategoryWithParts = {
          category: enrichedCategory,
          parts: existingCategory?.parts || [],
          isMech,
          price: mappedPrice,
          internalStock: existingCategory?.internalStock,
          totalSold: existingCategory?.totalSold,
          lastSoldDate: existingCategory?.lastSoldDate,
          lastSoldPrice: previousPricesByCategoryId.get(previousCategory.partCategoryId)?.lastSoldPrice,
        };

        const assembly = assemblies?.find((a: IPartAssembly) =>
          a.partAssemblyPartCategories?.some(pc => pc.partCategoryId === targetCategory.category.id)
        );

        const targetTableId = assembly ? assembly.id : "unmatched";
        const currentList = currentSelections[targetTableId] || [];
        const existingIndex = currentList.findIndex(c => c.category.id === targetCategory.category.id);

        if (existingIndex === -1) {
          currentSelections[targetTableId] = [...currentList, targetCategory];
          return;
        }

        const next = [...currentList];
        next[existingIndex] = {
          ...currentList[existingIndex],
          ...targetCategory,
          price: targetCategory.price ?? currentList[existingIndex].price,
        };
        currentSelections[targetTableId] = next;
      });

      Object.entries(currentSelections).forEach(([tableId, cats]) => {
        onTableSelectionChange(tableId, cats as CategoryWithParts[]);
      });

      setCategories(prev =>
        prev.map(cat => {
          const previousPrice = previousPricesByCategoryId.get(cat.category.id);
          if (!previousPrice) return cat;

          return {
            ...cat,
            price: previousPrice.price ?? cat.price,
            lastSoldPrice: previousPrice.lastSoldPrice ?? cat.lastSoldPrice,
            category: {
              ...cat.category,
              lastSoldPrice: previousPrice.lastSoldPrice ?? cat.category.lastSoldPrice,
            },
          };
        })
      );

      addNotification({
        primaryText: "Previous Part List Applied",
        secondaryText: `Matched by ${getPreviousPartListMatchLabel(previousPartListData.matchType)}`,
        variant: "success",
      });
      setPreviousPartListOpen(false);
    } catch (error) {
      console.error("Previous part list apply failed:", error);
      addNotification({ primaryText: "Previous Part List Apply Failed", variant: "error" });
    }
  };

  return {
    previousPartListOpen,
    setPreviousPartListOpen,
    previousPartListData,
    previousPartListsData,
    selectedPreviousJobId,
    setSelectedPreviousJobId,
    fetchPreviousPartListCategories,
    applyPreviousPartListCategories,
    previousPartListLoading: previousPartLists.isLoading,
  };
}
